import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LiveChatWidget = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isAgentOnline, setIsAgentOnline] = useState(true);
  const [agentName] = useState('Priya');
  const messagesEndRef = useRef(null);

  // Mock initial messages
  const initialMessages = [
    {
      id: 1,
      type: 'agent',
      content: 'Hi! I\'m Priya from the ShaadiNest support team. How can I help you today?',
      timestamp: new Date(),
      agent: {
        name: 'Priya',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9e3a56f?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80'
      }
    }
  ];

  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      setMessages(initialMessages);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage?.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: newMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    // Simulate agent response
    setTimeout(() => {
      setIsTyping(false);
      const agentResponse = {
        id: Date.now() + 1,
        type: 'agent',
        content: getAgentResponse(newMessage),
        timestamp: new Date(),
        agent: {
          name: 'Priya',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9e3a56f?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80'
        }
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1500);
  };

  const getAgentResponse = (message) => {
    const responses = {
      'booking': 'I can help you with vendor booking. What specific questions do you have about booking a vendor?',
      'payment': 'For payment issues, I can guide you through the payment process. What type of payment problem are you experiencing?',
      'vendor': 'I\'d be happy to help you find the right vendor. What type of vendor are you looking for and in which location?',
      'account': 'I can assist with account-related questions. What do you need help with regarding your account?',
      'cultural': 'Our platform specializes in South Asian wedding traditions. What cultural aspects can I help you with?',
      'default': 'Thank you for your message. Let me connect you with the right information to help you with that.'
    };

    const lowerMessage = message?.toLowerCase();
    
    if (lowerMessage.includes('booking') || lowerMessage.includes('book')) {
      return responses.booking;
    } else if (lowerMessage.includes('payment') || lowerMessage.includes('pay')) {
      return responses.payment;
    } else if (lowerMessage.includes('vendor')) {
      return responses.vendor;
    } else if (lowerMessage.includes('account') || lowerMessage.includes('login')) {
      return responses.account;
    } else if (lowerMessage.includes('cultural') || lowerMessage.includes('tradition')) {
      return responses.cultural;
    } else {
      return responses.default;
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const quickActions = [
    { label: 'Booking Help', value: 'I need help with booking a vendor' },
    { label: 'Payment Issues', value: 'I\'m having payment problems' },
    { label: 'Find Vendors', value: 'Help me find the right vendor' },
    { label: 'Account Help', value: 'I need help with my account' }
  ];

  const handleQuickAction = (action) => {
    setNewMessage(action.value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-80 h-96 bg-white rounded-xl shadow-strong border border-border z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-primary text-white rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1494790108755-2616b9e3a56f?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80"
              alt="Agent"
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
              isAgentOnline ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
          </div>
          <div>
            <p className="font-medium text-sm">{agentName}</p>
            <p className="text-xs opacity-90">
              {isAgentOnline ? 'Online' : 'Offline'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
        >
          <Icon name="X" size={18} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => (
          <div
            key={message?.id}
            className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-xs ${message?.type === 'user' ? 'order-2' : 'order-1'}`}>
              <div className={`p-3 rounded-lg ${
                message?.type === 'user' ?'bg-primary text-white' :'bg-surface-100 text-text-primary'
              }`}>
                <p className="text-sm">{message?.content}</p>
              </div>
              <p className="text-xs text-text-muted mt-1">
                {formatTime(message?.timestamp)}
              </p>
            </div>
            {message?.type === 'agent' && (
              <div className="order-1 mr-2">
                <img
                  src={message?.agent?.avatar}
                  alt={message?.agent?.name}
                  className="w-6 h-6 rounded-full object-cover"
                />
              </div>
            )}
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="flex items-center space-x-2 bg-surface-100 p-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce delay-200"></div>
              </div>
              <span className="text-text-muted text-sm">typing...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {messages?.length === 1 && (
        <div className="p-4 border-t border-border">
          <p className="text-text-muted text-xs mb-2">Quick actions:</p>
          <div className="grid grid-cols-2 gap-2">
            {quickActions?.map((action, index) => (
              <button
                key={index}
                onClick={() => handleQuickAction(action)}
                className="p-2 bg-surface-100 hover:bg-surface-200 rounded text-xs transition-colors"
              >
                {action?.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 text-sm"
            disabled={!isAgentOnline}
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            iconName="Send"
            disabled={!newMessage?.trim() || !isAgentOnline}
          />
        </div>
      </form>
    </div>
  );
};

export default LiveChatWidget;